import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import RefreshPromptModal from './RefreshPromptModal';

const LAST_TYPING_TIMESTAMP_KEY = 'lastUserTypingTimestamp';
const TYPING_GRACE_PERIOD_MS = 3000;

/**
 * Checks if the user is currently typing in an input field, textarea, or contenteditable element.
 * Also updates a timestamp in session storage if typing is detected.
 */
function isUserTyping(): boolean {
  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }
  const tagName = activeElement.tagName.toLowerCase();
  const isContentEditable = activeElement.hasAttribute('contenteditable') && activeElement.getAttribute('contenteditable') !== 'false';
  const typing = tagName === 'input' || tagName === 'textarea' || isContentEditable;
  if (typing) {
    sessionStorage.setItem(LAST_TYPING_TIMESTAMP_KEY, Date.now().toString());
  }
  return typing;
}

/**
 * Checks if the user was typing within the last `TYPING_GRACE_PERIOD_MS`.
 */
function wasUserTypingRecently(): boolean {
  if (isUserTyping()) return true; // Currently typing

  const lastTypingTimeStr = sessionStorage.getItem(LAST_TYPING_TIMESTAMP_KEY);
  if (!lastTypingTimeStr) {
    return false;
  }
  const lastTypingTime = parseInt(lastTypingTimeStr, 10);
  return (Date.now() - lastTypingTime) < TYPING_GRACE_PERIOD_MS;
}

/**
 * FocusHandler component to address the specific issue of infinite spinners
 * after tab switches by refreshing the page on the first navigation attempt
 * after returning to the tab.
 */
export function FocusHandler() {
  const location = useLocation();
  const navigate = useNavigate(); // For potential future use if modal needs to control navigation
  const queryClient = useQueryClient();
  const wasHidden = useRef(false);
  const [showRefreshPrompt, setShowRefreshPrompt] = useState(false);
  const [navigationCancelledByPrompt, setNavigationCancelledByPrompt] = useState(false);

  // Track tab visibility without immediate refresh
  useEffect(() => {
    let lastHiddenTime = 0;
    const TAB_SWITCH_CONTEXT_TIMEOUT = 5000; // ms, how long the "came from tab switch" context lasts

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('FocusHandler: Tab hidden.');
        sessionStorage.setItem('tabHiddenTimestamp', Date.now().toString());
        wasHidden.current = true; // Use ref to track if tab was hidden in this session
      } else if (wasHidden.current) { // Only act if tab was hidden then refocused
        const hiddenTimestamp = parseInt(sessionStorage.getItem('tabHiddenTimestamp') || '0', 10);
        const timeAwayInSeconds = (Date.now() - hiddenTimestamp) / 1000;
        console.log(`FocusHandler: Tab refocused after ${timeAwayInSeconds.toFixed(1)}s. Setting tab switch context.`);
        // Set a flag that indicates a recent tab switch, with a short expiry
        sessionStorage.setItem('recentTabSwitch', 'true'); // Short-lived for general awareness
        sessionStorage.setItem('navigationFollowsRecentTabSwitch', 'true'); // For the next navigation
        console.log('FocusHandler: Set recentTabSwitch (5s) and navigationFollowsRecentTabSwitch (persistent until consumed) flags.');

        setTimeout(() => {
          sessionStorage.removeItem('recentTabSwitch');
          console.log('FocusHandler: Cleared short-lived recentTabSwitch flag.');
        }, TAB_SWITCH_CONTEXT_TIMEOUT);
        wasHidden.current = false; // Reset ref
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Intercept navigation after tab switch for proactive query invalidation
  useEffect(() => {
    const isFullPageNavigation =
      sessionStorage.getItem('lastPathnameForFocusHandler') !== location.pathname;

    if (isFullPageNavigation) {
      sessionStorage.setItem('lastPathnameForFocusHandler', location.pathname);

      // Check if this navigation is the one flagged to follow a recent tab switch
      if (sessionStorage.getItem('navigationFollowsRecentTabSwitch') === 'true') {
        if (wasUserTypingRecently()) {
          console.log('FocusHandler: User was typing recently, deferring proactive cache reset for navigation after tab switch.');
          // Do not consume 'navigationFollowsRecentTabSwitch' yet, try on next non-typing nav or let stuck nav handle.
          return;
        }
        // Instead of invalidating queries, show the refresh prompt
        console.log(`FocusHandler: Showing refresh prompt for path ${location.pathname} because 'navigationFollowsRecentTabSwitch' is set.`);
        setShowRefreshPrompt(true);
        setNavigationCancelledByPrompt(true); // Prevent stuck nav timer from firing for this navigation
        
        // Consume the flag. If user chooses "Continue Loading", normal flow resumes but without this prompt again for this instance.
        sessionStorage.removeItem('navigationFollowsRecentTabSwitch'); 
        console.log('FocusHandler: Consumed navigationFollowsRecentTabSwitch flag; showing refresh prompt.');
      }
    }
  }, [location, queryClient, navigate]); // Added navigate to dependencies

  // Fall-back safety net for stuck navigation
  useEffect(() => {
    const effectStartTime = Date.now();
    const isThisNavigationFollowingTabSwitch = sessionStorage.getItem('navigationFollowsRecentTabSwitch') === 'true';

    console.log(`FocusHandler (Stuck Nav Effect @${effectStartTime}): useEffect for location ${location.pathname} triggered. 'navigationFollowsRecentTabSwitch' is currently ${isThisNavigationFollowingTabSwitch}. 'recentTabSwitch' is ${sessionStorage.getItem('recentTabSwitch')}`);
    
    // If the refresh prompt is shown, this stuck navigation logic should not run for this navigation attempt
    if (navigationCancelledByPrompt) {
      console.log(`FocusHandler (Stuck Nav Effect @${effectStartTime}): Stuck navigation monitoring SKIPPED for ${location.pathname} because refresh prompt was shown.`);
      setNavigationCancelledByPrompt(false); // Reset flag for next navigation
      return undefined; 
    }

    const navStartTime = Date.now();
    const navigationKey = `navigation-attempt-${navStartTime}`;

    const isFullPageNavigation =
      sessionStorage.getItem('lastMonitoredPathnameForStuckNav') !== location.pathname;

    if (isFullPageNavigation) {
      sessionStorage.setItem('lastMonitoredPathnameForStuckNav', location.pathname);
      sessionStorage.setItem(navigationKey, navStartTime.toString());

      const useAggressiveTimeout = sessionStorage.getItem('recentTabSwitch') === 'true';
      const stuckNavTimeout = useAggressiveTimeout ? 10000 : 25000;

      if (useAggressiveTimeout) {
        console.log(`FocusHandler (Stuck Nav @${Date.now()}): 'recentTabSwitch' is active. Using aggressive timeout: ${stuckNavTimeout}ms for ${location.pathname}`);
      } else {
        console.log(`FocusHandler (Stuck Nav @${Date.now()}): 'recentTabSwitch' is NOT active. Using default timeout: ${stuckNavTimeout}ms for ${location.pathname}`);
      }
      
      const navigationTimeout = setTimeout(() => {
        const attemptTime = sessionStorage.getItem(navigationKey);
        if (attemptTime) {
          if (wasUserTypingRecently()) {
            console.log('FocusHandler: User was typing recently, deferring stuck navigation fallback suggestion.');
            sessionStorage.removeItem(navigationKey);
            return;
          }
          const timeElapsed = (Date.now() - parseInt(attemptTime)) / 1000;
          console.error(`FocusHandler: Navigation stuck for ${timeElapsed.toFixed(1)}s. Suggesting recovery. Stuck path: ${location.pathname}`);
          sessionStorage.setItem('recoverySuggested_FocusHandler', `Stuck navigation to ${location.pathname} after ${timeElapsed.toFixed(1)}s`);
        }
      }, stuckNavTimeout);

      return () => {
        clearTimeout(navigationTimeout);
        sessionStorage.removeItem(navigationKey);
      };
    }

    return undefined;
  }, [location.pathname, navigationCancelledByPrompt]); // Added navigationCancelledByPrompt

  const handleRefresh = () => {
    setShowRefreshPrompt(false);
    window.location.reload();
  };

  const handleClosePrompt = () => {
    setShowRefreshPrompt(false);
    // Allow the navigation to proceed (or attempt to) without the prompt.
    // The stuck navigation timer might still catch it if it's slow.
    // We've already consumed the 'navigationFollowsRecentTabSwitch' flag.
    console.log('FocusHandler: User chose "Continue Loading". Navigation will proceed if it hasn\'t already.');
  };

  return (
    <>
      <RefreshPromptModal
        isOpen={showRefreshPrompt}
        onClose={handleClosePrompt}
        onRefresh={handleRefresh}
      />
    </>
  );
}
